import {
  CloudRain,
  FilePlus2,
  Layers,
  LocateFixed,
  Minus,
  Plus,
  TriangleAlert,
  Wind,
} from "lucide-react";
import type { Map as LeafletMap } from "leaflet";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useMediaQuery } from "../useMediaQuery.ts";
import { useSession } from "../admin/session.ts";
import { getApiMode } from "../api/config.ts";
import { useLocale } from "../i18n/index.ts";
import { IconButton } from "../components/Button.tsx";
import { L } from "./leaflet-global.ts";
import { prefersReducedMotion } from "./OverviewMap.tsx";
import { COVERAGE_BOUNDS } from "./tiles.ts";

interface Props {
  map: LeafletMap | null;
  /** Phones: the Karte/Luftbild switch lives in this column as one toggle (the top bar has no room). */
  aerial?: { on: boolean; onToggle: () => void } | undefined;
  /** Wind overlay switch; `disabled` while offline (the forecast needs the network). */
  wind?: { on: boolean; onToggle: () => void; disabled: boolean } | undefined;
  /** Precipitation overlay switch, same rules as wind. */
  precip?: { on: boolean; onToggle: () => void; disabled: boolean } | undefined;
}

/** Clear-glass zoom +/- and locate controls. Geolocation denial is reported, never thrown. */
export function MapControls({ map, aerial, wind, precip }: Props) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { staff } = useSession();
  // phones (portrait and landscape) zoom with their fingers: no +/- buttons there; tablets and desktops keep them
  const phone = useMediaQuery(
    "(max-width: 599.98px), (max-width: 899.98px) and (max-height: 450px)",
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const layerRef = useRef<ReturnType<typeof L.layerGroup> | null>(null);

  useEffect(() => {
    if (!notice) return;
    const id = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(id);
  }, [notice]);

  useEffect(
    () => () => {
      layerRef.current?.remove();
    },
    [],
  );

  const locate = () => {
    if (!map) return;
    if (!("geolocation" in navigator)) {
      setNotice(t("map.locateUnavailable"));
      return;
    }
    setBusy(true);
    setNotice(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusy(false);
        const ll = L.latLng(pos.coords.latitude, pos.coords.longitude);
        // the map ends at the swisstopo coverage: a position outside it cannot be shown
        if (!L.latLngBounds(COVERAGE_BOUNDS).contains(ll)) {
          setNotice(t("map.locateOutside"));
          return;
        }
        layerRef.current?.remove();
        layerRef.current = L.layerGroup([
          L.circle(ll, {
            radius: Math.max(pos.coords.accuracy, 10),
            className: "locate-halo",
            interactive: false,
          }),
          L.circleMarker(ll, { radius: 8, className: "locate-dot", interactive: false }),
        ]).addTo(map);
        const zoom = Math.max(map.getZoom(), 14);
        if (prefersReducedMotion()) map.setView(ll, zoom, { animate: false });
        else map.flyTo(ll, zoom, { duration: 0.9 });
      },
      (err) => {
        setBusy(false);
        setNotice(
          err.code === err.PERMISSION_DENIED ? t("map.locateDenied") : t("map.locateUnavailable"),
        );
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  };

  return (
    <>
      <nav className="map-controls" aria-label={t("map.controls")}>
        {aerial ? (
          <IconButton
            aria-label={t("map.layerAerial")}
            title={t("map.layerAerial")}
            aria-pressed={aerial.on}
            className="map-layer-toggle"
            icon={<Layers aria-hidden="true" strokeWidth={1.75} />}
            onClick={aerial.onToggle}
          />
        ) : null}
        {wind ? (
          <IconButton
            aria-label={t("wind.toggle")}
            title={wind.disabled ? t("wind.offline") : t("wind.toggle")}
            aria-pressed={wind.on}
            disabled={wind.disabled}
            className="map-wind-toggle"
            icon={<Wind aria-hidden="true" strokeWidth={1.75} />}
            onClick={wind.onToggle}
          />
        ) : null}
        {precip ? (
          <IconButton
            aria-label={t("precip.toggle")}
            title={precip.disabled ? t("precip.offline") : t("precip.toggle")}
            aria-pressed={precip.on}
            disabled={precip.disabled}
            className="map-precip-toggle"
            icon={<CloudRain aria-hidden="true" strokeWidth={1.75} />}
            onClick={precip.onToggle}
          />
        ) : null}
        {phone ? null : (
          <>
            <IconButton
              aria-label={t("map.zoomIn")}
              className="map-zoom"
              icon={<Plus aria-hidden="true" strokeWidth={1.75} />}
              onClick={() => map?.zoomIn()}
            />
            <IconButton
              aria-label={t("map.zoomOut")}
              className="map-zoom"
              icon={<Minus aria-hidden="true" strokeWidth={1.75} />}
              onClick={() => map?.zoomOut()}
            />
          </>
        )}
        <IconButton
          aria-label={t("map.locate")}
          aria-busy={busy}
          icon={<LocateFixed aria-hidden="true" strokeWidth={1.75} />}
          onClick={locate}
        />
        {getApiMode() === "live" && !staff ? (
          <Link
            to={`/${locale}/konto/vorschlagen/spot`}
            aria-label={t("suggest.spotButton")}
            title={t("suggest.spotButton")}
            className="ds-btn ds-btn--icon ds-btn--glass ds-glass ds-glass--clear"
          >
            <FilePlus2 aria-hidden="true" strokeWidth={1.75} />
          </Link>
        ) : null}
      </nav>
      {notice ? (
        <div className="map-toast ds-glass ds-text-subheadline" role="status">
          <TriangleAlert
            aria-hidden="true"
            size={20}
            strokeWidth={1.75}
            className="text-warning shrink-0"
          />
          <span>{notice}</span>
        </div>
      ) : null}
    </>
  );
}
